/**
 * TypingIndicator — ChatGPT-style "thinking" surface.
 *
 * Phase labels and nothing else: ``Reading {filename}…`` →
 * ``Working on it…`` → ``Thinking…``. The component intentionally rejects
 * leaking internal pipeline details (chunk counts, embedding stage,
 * token counts, model IDs).
 *
 *   - Pulsing dot before the text: 1s scale+opacity loop (clearly visible
 *     to the user as a "breathing" indicator that something is happening).
 *   - Phase swap: cross-fade with a 4px translateY entry. Driven by the
 *     ``key`` swap inside LiveStatusLine.
 *   - RTL: every layout property uses logical CSS, the line is dir="auto".
 *   - prefers-reduced-motion: animations collapse to instantaneous swaps.
 *
 * Label wording is shared with the persistent status line via
 * utils/progressStatus.phaseLabel — one source of truth for both surfaces.
 */

import { useLanguage } from '@hooks/useLanguage';
import { LiveStatusLine } from './LiveStatusLine';
import { phaseLabel, type ThinkingPhaseLit } from '../utils/progressStatus';
import './TypingIndicator.css';

export type ThinkingPhase = ThinkingPhaseLit;

interface TypingIndicatorProps {
  /** Active phase. Defaults to ``thinking`` — the common case. */
  phase?: ThinkingPhase;
  /** Original filename surfaced during the per-file phases. Never a file_id
   *  or storage path. */
  filename?: string;
  /** Explicit override label (e.g. live progress sentences like
   *  "Reading wiki page 3/12…"). When set, it replaces the phase label;
   *  changing it cross-fades to the new sentence. */
  text?: string;
  /** Epoch ms when the current status started — shows the "· Ns" elapsed
   *  counter for long single steps. */
  since?: number;
}

export function TypingIndicator({
  phase = 'thinking',
  filename,
  text,
  since,
}: TypingIndicatorProps) {
  const { t } = useLanguage();
  const label = text || phaseLabel(t, phase, filename);

  return (
    <div className="message-group assistant">
      <div className="message-content">
        <LiveStatusLine text={label} since={since} />
      </div>
    </div>
  );
}
